"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronRight } from "@/components/icons";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { DealSummary } from "@convex/listings/queries";
import { Button } from "@/components/ui/button";
import { formatCents } from "@/lib/formatting/currency";

type HistoryPage = {
  history: ReadonlyArray<DealSummary>;
  nextHistoryOffset: number | null;
  historyTotalCount: number;
};

export function BuyerDealHistory({
  productId,
  page,
}: {
  productId: Id<"products">;
  page: HistoryPage;
}) {
  if (page.historyTotalCount === 0) return null;
  return (
    <details className="rounded-md border border-border/60">
      <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
        <span className="flex items-center justify-between">
          Earlier on this listing ({page.historyTotalCount})
          <ChevronRight className="h-3.5 w-3.5 transition-transform" />
        </span>
      </summary>
      <HistoryRows items={page.history} />
      <NextHistoryPage
        key={page.nextHistoryOffset}
        productId={productId}
        offset={page.nextHistoryOffset}
      />
    </details>
  );
}

function HistoryRows({ items }: { items: ReadonlyArray<DealSummary> }) {
  return (
    <ul className="divide-y divide-border/60 text-xs">
      {items.map((deal) => (
        <li key={`${deal.kind}:${deal.id}`}>
          <Link
            href={deal.href}
            className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-muted/40"
          >
            <span className="text-foreground">{deal.statusLabel}</span>
            <span className="text-muted-foreground">
              {deal.totalCents !== undefined
                ? formatCents(deal.totalCents)
                : null}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NextHistoryPage({
  productId,
  offset,
}: {
  productId: Id<"products">;
  offset: number | null;
}) {
  const [requested, setRequested] = useState(false);
  const page = useQuery(
    api.listings.queries.getDealsOnListing,
    requested && offset !== null
      ? { productId, historyOffset: offset }
      : "skip",
  );
  if (offset === null || page === null) return null;
  if (page)
    return (
      <>
        <HistoryRows items={page.history} />
        <NextHistoryPage
          key={page.nextHistoryOffset}
          productId={productId}
          offset={page.nextHistoryOffset}
        />
      </>
    );
  return (
    <Button
      variant="ghost"
      size="sm"
      className="w-full"
      disabled={requested}
      onClick={() => setRequested(true)}
    >
      {requested ? "Loading history…" : "Load more history"}
    </Button>
  );
}
